import type { DirectusAssetBooking } from '@/api/calls/directus-calls/asset-bookings-calls'
import generateUniqueLocalID from '@/utils/generateUniqueLocalID'
import getAssetSrc from '@/utils/getAssetSrc'
import parseIntOrFloat from '@/utils/parseIntOrFloat'
import { getNiceDuration } from '@/utils/timeUtils'
import { DateTime } from 'luxon'
import Charger from './charger_classes/charger'
import Vehicle from './vehicle_classes/vehicle'

// error messages
export const error_invalid_asset_booking_data =
  'AssetBookingData is required to create an AssetBooking object.'
export const error_invalid_asset_booking_asset_id = 'AssetBooking asset_id is invalid.'
export const error_invalid_asset_booking_asset_type = 'AssetBooking asset_type is invalid.'
export const error_invalid_asset_booking_user_id = 'AssetBooking user_id is invalid.'
export const error_asset_booking_start_date_invalid = 'AssetBooking start_date is invalid.'
export const error_asset_booking_end_date_invalid = 'AssetBooking end_date is invalid.'
export const error_asset_booking_end_before_start = 'AssetBooking end_date cannot be before start_date.'

// class types
interface AssetBookingData {
  local_id?: string
  external_id?: string
  start_date: Date
  end_date: Date
  asset_type: AssetType
  asset_id: string | number
  user_id: string
  asset_reference?: Vehicle | Charger
}

type AssetType = 'vehicle' | 'charger'

type AssetReference = Vehicle | Charger

export default class AssetBooking {
  // ----------------------------------------------------------------------- //
  // -------------------------- Global class state ------------------------- //
  // ----------------------------------------------------------------------- //

  /** global record of class instance ids this session. */
  static usedIds: string[] = []

  // ----------------------------------------------------------------------- //
  // ------------------------------- State --------------------------------- //
  // ----------------------------------------------------------------------- //

  /** local session scope unique id for this asset booking. */
  local_id?: string

  /**
   * Optional external DB id for the saved asset booking record.
   *
   * NOTE: Currently refers to the directus `Asset_Bookings` collection record id.
   */
  external_id?: string

  /** The start date of the asset booking. */
  start_date: Date

  /** The end date of the asset booking. */
  end_date: Date

  /** The type of asset this booking is for. */
  asset_type: AssetType

  /**
   * The id of the asset that this booking is for.
   *
   * NOTE: This is the primary key of the asset and depending which asset we are
   * referring to it can be either a int, bigInt (passed as a string in json from
   * directus) or UUID.
   */
  asset_id: string | number

  /**
   * The id of the user that this booking is for.
   *
   * NOTE: This relates to the directus `directus_users` collection record id.
   */
  user_id: string

  /** The asset that this booking is for. */
  asset_reference?: AssetReference

  // ----------------------------------------------------------------------- //
  // ---------------------------- Constructor ------------------------------ //
  // ----------------------------------------------------------------------- //

  /**
   * Constructor for creating a new AssetBooking object.
   *
   * @param {AssetBookingData} assetBookingData - The data to initialize the AssetBooking object.
   *
   * The constructor will initialize the state of the object and add the object's local_id
   * to the list of used unique ids.
   *
   * If the id already exists in the list of used ids, it is assumed that this is an overwrite
   * to the original object.
   */
  constructor(assetBookingData: AssetBookingData) {
    // sanity check data
    if (!assetBookingData) {
      throw new Error(error_invalid_asset_booking_data)
    }
    if (assetBookingData.asset_id === undefined || assetBookingData.asset_id === null)
      throw new Error(error_invalid_asset_booking_asset_id)
    if (
      !assetBookingData.asset_type ||
      (assetBookingData.asset_type !== 'vehicle' && assetBookingData.asset_type !== 'charger')
    )
      throw new Error(error_invalid_asset_booking_asset_type)
    if (!assetBookingData.user_id) throw new Error(error_invalid_asset_booking_user_id)
    if (
      !(assetBookingData.start_date instanceof Date) ||
      isNaN(assetBookingData.start_date.getTime())
    )
      throw new Error(error_asset_booking_start_date_invalid)
    if (!(assetBookingData.end_date instanceof Date) || isNaN(assetBookingData.end_date.getTime()))
      throw new Error(error_asset_booking_end_date_invalid)
    if (assetBookingData.end_date.getTime() < assetBookingData.start_date.getTime()) {
      throw new Error(error_asset_booking_end_before_start)
    }

    // initialize state
    this.local_id =
      assetBookingData?.local_id ?? generateUniqueLocalID(AssetBooking.usedIds, 'asset-booking')
    this.external_id = assetBookingData?.external_id
    this.start_date = assetBookingData.start_date
    this.end_date = assetBookingData.end_date
    this.asset_type = assetBookingData.asset_type
    this.asset_id = assetBookingData.asset_id
    this.user_id = assetBookingData.user_id
    this.asset_reference = assetBookingData.asset_reference

    // add id to list of used unique ids
    // ASSUMES: if id already exists this is an overwrite to the original
    // object.
    if (!AssetBooking.usedIds.includes(this.local_id)) {
      AssetBooking.usedIds.push(this.local_id)
    }
  }

  /**
   * Converts saved asset booking data to a new AssetBooking instance.
   *
   * @param {DirectusAssetBooking} savedData - The saved asset booking data to convert.
   * @return {AssetBooking} A new AssetBooking instance created from the saved data.
   */
  static fromSavedData(savedData: DirectusAssetBooking): AssetBooking {
    // parse asset id
    let asset_id: string | number | undefined = undefined
    // try parsing number first
    if (
      savedData.asset_id &&
      (typeof savedData.asset_id === 'string' || typeof savedData.asset_id === 'number')
    ) {
      asset_id = parseIntOrFloat(savedData.asset_id)
    }
    // if that fails, try assigning as string
    if (!asset_id && savedData.asset_id && typeof savedData.asset_id === 'string') {
      asset_id = savedData.asset_id
    }
    // validate asset id
    if (!asset_id) {
      throw new Error(error_invalid_asset_booking_asset_id)
    }

    // prepare data
    const data: AssetBookingData = {
      external_id: savedData.id,
      start_date: new Date(savedData.start),
      end_date: new Date(savedData.end),
      asset_type: savedData.asset_type as AssetType,
      asset_id,
      user_id: savedData.user_id,
    }

    // return new instance
    return new AssetBooking(data)
  }

  // ----------------------------------------------------------------------- //
  // ------------------------------- Getters ------------------------------- //
  // ----------------------------------------------------------------------- //

  /**
   * Indicates whether the booking is in the past, present, or future.
   * @returns {'past' | 'present' | 'future'} The booking state.
   *
   * Booking time interval is treated as inclusive: [start_date, end_date].
   */
  public get bookingState(): 'past' | 'present' | 'future' {
    const now = Date.now()

    if (this.end_date.getTime() < now) return 'past'
    if (this.start_date.getTime() > now) return 'future'
    return 'present'
  }

  /**
   * Returns the duration of the booking in seconds.
   *
   * @return {number} The duration of the booking in seconds.
   */
  public get duration(): number {
    return Math.round((this.end_date.getTime() - this.start_date.getTime()) / 1000)
  }

  /**
   * Returns a human-readable string representing the duration of the booking.
   *
   * The string will be in the format 'X hours Y minutes' or 'X minutes' or 'X seconds'.
   *
   * @return {string} A human-readable string representing the duration of the booking.
   */
  public get formattedDuration(): string {
    return getNiceDuration(this.duration)
  }

  /**
   * Returns a human-readable string representing the start date of the booking.
   *
   * The string will be in the format 'Weekday, Day Month Year' (e.g. 'Monday, 10 January 2022').
   *
   * @return {string} A human-readable string representing the start date of the booking.
   */
  public get formattedStartDate(): string {
    return DateTime.fromJSDate(this.start_date).toLocaleString({
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })
  }

  /**
   * Returns a human-readable string representing the end date of the booking.
   *
   * The string will be in the format 'Weekday, Day Month Year' (e.g. 'Monday, 10 January 2022').
   *
   * @return {string} A human-readable string representing the end date of the booking.
   */
  public get formattedEndDate(): string {
    return DateTime.fromJSDate(this.end_date).toLocaleString({
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })
  }

  /**
   * Returns a human-readable string representing the start time of the booking.
   *
   * The string will be in the format `h:mm tt` (e.g. 2:15pm).
   *
   * @return {string} A human-readable string representing the start time of the booking.
   */
  public get formattedStartTime(): string {
    return DateTime.fromJSDate(this.start_date).toLocaleString({
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  /**
   * Returns a human-readable string representing the end time of the booking.
   *
   * The string will be in the format `h:mm tt` (e.g. 2:15 pm).
   *
   * @return {string} A human-readable string representing the end time of the booking.
   */
  public get formattedEndTime(): string {
    return DateTime.fromJSDate(this.end_date).toLocaleString({
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  /**
   * Returns a human-readable string representing the booking.
   *
   * The string will be in the format 'Charger booked at [charger name]' or 'Vehicle booking'.
   *
   * @return {string} A human-readable string representing the booking.
   */
  public get description(): string {
    // if asset reference is charger
    if (this.asset_reference instanceof Charger) {
      const displayName = this.asset_reference.name || this.asset_reference.addressString
      return displayName ? 'Charger booked at ' + displayName : 'Charger booking'
    }

    // if asset reference is vehicle
    if (this.asset_reference instanceof Vehicle) {
      return (
        this.asset_reference.licensePlate ||
        this.asset_reference.name ||
        this.asset_reference.evModel?.name ||
        'Vehicle booking'
      )
    }

    // fallback to empty
    return ''
  }

  /**
   * Returns the full src path to the image for this booking if there is one.
   *
   * The image will be the first uploaded image to the vehicle, or the model image if the vehicle has an EV model.
   * If the above fails, the image will be the default car image.
   *
   * @return {string | undefined} A human-readable string representing the image src for this booking.
   */
  public get displayImgSrc(): string | undefined {
    let imgSrc: string | undefined = undefined

    if (this.asset_reference instanceof Charger) {
      // todo: add charger image
    }
    if (this.asset_reference instanceof Vehicle) {
      // get uploaded image to car in first instance.
      const images = this.asset_reference.imageSrcPaths
      if (images.length) {
        imgSrc = images[0]
      }
      // get model image in second instance.
      else if (this.asset_reference.evModel && this.asset_reference.evModel.imageSrc) {
        imgSrc = this.asset_reference.evModel.imageSrc
      }
      // get default image if all else fails.
      else imgSrc = getAssetSrc('car_images/placeholder_car.png')
    }

    return imgSrc
  }

  // ----------------------------------------------------------------------- //
  // ------------------------------- Methods ------------------------------- //
  // ----------------------------------------------------------------------- //

  /**
   * Updates the asset booking data from a partial directus asset booking.
   *
   * The function will return early if the input data is undefined or null.
   *
   * The function will then prepare the data by creating a new object with the same properties as the asset booking data.
   * It will then assign the values from the input data to the corresponding properties in the new object.
   * If the input data contains an asset id, it will be parsed as a number first and then as a string if the parsing fails.
   *
   * Finally, the function will call the update method with the prepared data.
   *
   * @param {Partial<DirectusAssetBooking>} data - The partial directus asset booking data to update from.
   */
  public updateFromDirectusData(data: Partial<DirectusAssetBooking>): void {
    if (!data) return

    // prepare data
    const formattedData: Partial<AssetBookingData> = {
      external_id: data.id,
    }

    if (data.start) formattedData.start_date = new Date(data.start)
    if (data.end) formattedData.end_date = new Date(data.end)
    if (data.asset_type) formattedData.asset_type = data.asset_type as AssetType
    if (data.asset_id) {
      // parse asset id
      let asset_id: string | number | undefined = undefined
      // try parsing number first
      if (
        data.asset_id &&
        (typeof data.asset_id === 'string' || typeof data.asset_id === 'number')
      ) {
        asset_id = parseIntOrFloat(data.asset_id)
      }
      // if that fails, try assigning as string
      if (!asset_id && data.asset_id && typeof data.asset_id === 'string') {
        asset_id = data.asset_id
      }
      formattedData.asset_id = asset_id
    }
    if (data.user_id) formattedData.user_id = data.user_id

    Object.assign(this, formattedData)
  }
}
